/**
 * View of a page: editable title + block editor with autosave,
 * icon, organization sharing, backlinks, and attachments.
 * It is mounted keyed by pageId (the route remounts the component on navigation).
 */
import { createMemo, createSignal, Show } from "solid-js";
import { AlertCircle, Check, History, Loader2, Share2, Users } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import type { PageVersion } from "@apunte-digital/powersync";
import { usePageBlocksQuery, usePageDetailQuery } from "@apunte-digital/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PullToDismiss } from "@/components/ui/pull-to-dismiss";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/features/notes/components/icon-picker";
import { PageEditor } from "./page-editor";
import { PageShareDialog } from "./page-share-dialog";
import { VersionHistorySheet } from "./version-history-sheet";
import { PageBacklinks } from "./page-backlinks";
import { PageAttachments } from "./page-attachments";
import { PageBreadcrumbs } from "./page-breadcrumbs";
import { usePageEditor } from "../hooks/use-page-editor";
import { useNoteCarousel } from "../hooks/use-note-carousel";
import { flattenPageTree, usePageTree } from "../hooks/use-page-tree";
import { AppErrorBoundary } from "@/components/error-boundary";

const TITLE_SAVE_DEBOUNCE_MS = 400;

interface NotePageViewProps {
  pageId: string;
  onRename: (id: string, title: string) => void;
  onSetIcon: (id: string, icon: string | null) => void;
  /** Restore a version from history (forward write). */
  onRestoreVersion: (id: string, version: PageVersion) => Promise<void>;
}

export function NotePageView(props: NotePageViewProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();

  const page = usePageDetailQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // Resolves the page: present / loading (sync) / not-found / offline. Prevents
  // opening a deleted page, an invalid id, or a wiki-link to a nonexistent
  // page from leaving the editor loading forever with an empty "Untitled" title.
  // A page in the trash is treated as not-found in the detail
  // (it is managed from /trash; its wiki-links stop resolving).
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(page.data && page.data.deleted_at === null),
    isPending: () => page.isLoading,
  });

  const editorState = usePageEditor(() => props.pageId);

  // URL-addressable overlays (`?share=1`, `?icon=1`): the query is the
  // source of truth, so a copied link reopens exactly the same dialog.
  // Idempotent: `replace` does not bloat history, and opening/closing only toggles
  // the relevant search param while preserving the rest of the query.
  const urlSearch = useSearch({ strict: false }) as () => {
    share?: boolean;
    icon?: boolean;
    versions?: boolean;
    attachment?: string;
  };
  const setShareOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, share: open ? true : undefined }),
      replace: true,
    });
  };
  const setIconOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, icon: open ? true : undefined }),
      replace: true,
    });
  };
  const shareOpen = () => urlSearch().share === true;
  const iconOpen = () => urlSearch().icon === true;
  const setVersionsOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, versions: open ? true : undefined }),
      replace: true,
    });
  };
  const versionsOpen = () => urlSearch().versions === true;

  // Current blocks (reference for the history diff). Shares a queryKey
  // (and therefore the cache) with the editor subscription.
  const currentBlocks = usePageBlocksQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
  });
  const [restoring, setRestoring] = createSignal(false);
  const handleRestore = async (version: PageVersion) => {
    if (restoring()) {
      return;
    }
    setRestoring(true);
    try {
      await props.onRestoreVersion(props.pageId, version);
      setVersionsOpen(false);
    } finally {
      setRestoring(false);
    }
  };

  // ── Carousel between sibling notes (horizontal swipe) ──
  // The flat tree list (sidebar visual order) defines "previous" and
  // "next"; swiping navigates to the adjacent page. The route remounts the detail view.
  const { tree } = usePageTree();
  const flatPages = createMemo(() => flattenPageTree(tree()));
  const currentIndex = createMemo(() =>
    flatPages().findIndex((entry) => entry.id === props.pageId),
  );
  const neighborId = (offset: number): string | null => {
    const index = currentIndex();
    if (index < 0) {
      return null;
    }
    return flatPages()[index + offset]?.id ?? null;
  };

  let surfaceRef: HTMLDivElement | undefined;
  const carousel = useNoteCarousel({
    getPrevId: () => neighborId(-1),
    getNextId: () => neighborId(1),
    navigate: (pageId) => {
      void navigate({ to: "/notes/$pageId", params: { pageId } });
    },
    getSurfaceRef: () => surfaceRef,
  });

  // The title uses "latest local edit wins" while typing; when there is
  // no edit in progress, it reflects the synced value.
  const [draftTitle, setDraftTitle] = createSignal<string | null>(null);
  let titleTimeout: ReturnType<typeof setTimeout> | null = null;

  const title = createMemo(() => draftTitle() ?? page.data?.title ?? "");

  const handleTitleInput = (value: string) => {
    setDraftTitle(value);
    if (titleTimeout) {
      clearTimeout(titleTimeout);
    }
    titleTimeout = setTimeout(() => {
      titleTimeout = null;
      props.onRename(props.pageId, value);
      setDraftTitle(null);
    }, TITLE_SAVE_DEBOUNCE_MS);
  };

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <div class="flex h-full min-w-0 flex-1 items-center justify-center">
          <NotFoundState
            variant={resolution() === "offline" ? "offline" : "not-found"}
            title={resolution() === "offline" ? undefined : t(msg`Note not found`)}
            description={
              resolution() === "offline"
                ? undefined
                : t(msg`This note may have been deleted, or you don't have access to it.`)
            }
            primaryAction={{
              label: t(msg`Back to notes`),
              onClick: () => {
                void navigate({ to: "/notes" });
              },
            }}
          />
        </div>
      }
    >
      <div
        ref={(element) => {
          surfaceRef = element;
        }}
        class="flex h-full min-w-0 flex-1 flex-col overflow-y-auto"
        style={{ "touch-action": "pan-y" }}
        onPointerDown={carousel.handlePointerDown}
        onPointerMove={carousel.handlePointerMove}
        onPointerUp={carousel.handlePointerUp}
        onPointerCancel={carousel.handlePointerCancel}
      >
        <div
          class="mx-auto flex w-full max-w-3xl flex-1 flex-col px-6 py-8 lg:px-10"
          style={{
            transform:
              carousel.dragX() === 0 ? undefined : `translate3d(${carousel.dragX()}px, 0, 0)`,
            transition: carousel.isAnimating()
              ? `transform ${carousel.transitionMs}ms cubic-bezier(0.32, 0.72, 0, 1)`
              : undefined,
            "will-change": carousel.dragX() === 0 ? "auto" : "transform",
          }}
        >
          {/* Tree breadcrumbs: the way back up (interconnection). */}
          <PageBreadcrumbs pageId={props.pageId} />
          <div class="flex items-center gap-2">
            <Popover open={iconOpen()} onOpenChange={setIconOpen}>
              <PopoverTrigger
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-2xl transition-transform motion-safe:will-change-transform hover:bg-accent/10 active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                aria-label={t(msg`Page icon`)}
                title={t(msg`Page icon`)}
              >
                <Show when={page.data?.icon} fallback={<span class="opacity-40">📄</span>}>
                  {page.data?.icon}
                </Show>
              </PopoverTrigger>
              <PopoverContent class="w-72">
                {/* Close by vertical swipe (touch): same gesture standard. */}
                <PullToDismiss onDismiss={() => setIconOpen(false)}>
                  <IconPicker
                    selectedIcon={page.data?.icon ?? ""}
                    onSelect={(icon) => {
                      props.onSetIcon(props.pageId, icon === page.data?.icon ? null : icon);
                      setIconOpen(false);
                    }}
                  />
                </PullToDismiss>
              </PopoverContent>
            </Popover>
            <input
              type="text"
              value={title()}
              onInput={(e) => {
                handleTitleInput(e.currentTarget.value);
              }}
              placeholder={t(msg`Untitled`)}
              class="w-full bg-transparent text-3xl font-bold tracking-tight text-foreground outline-none placeholder:text-muted-foreground/40"
              aria-label={t(msg`Page title`)}
            />
            {/* Feedback de autosave: guardando → error → guardado. */}
            <Show
              when={editorState.saveError()}
              fallback={
                <Show
                  when={editorState.isSaving()}
                  fallback={
                    <Show when={editorState.lastSavedAt()}>
                      <span
                        class="flex shrink-0 items-center gap-1 text-xs font-medium text-success motion-safe:animate-fade-in"
                        aria-live="polite"
                      >
                        <Check class="h-3.5 w-3.5" />
                        <span class="hidden sm:inline">{t(msg`Saved`)}</span>
                      </span>
                    </Show>
                  }
                >
                  <span
                    class="flex shrink-0 items-center gap-1 text-xs text-muted-foreground/60"
                    aria-live="polite"
                  >
                    <Loader2 class="h-3.5 w-3.5 animate-spin" />
                    <span class="hidden sm:inline">{t(msg`Saving…`)}</span>
                  </span>
                </Show>
              }
            >
              <span
                class="flex shrink-0 items-center gap-1 text-xs font-medium text-danger"
                aria-live="assertive"
                role="status"
              >
                <AlertCircle class="h-3.5 w-3.5" />
                <span class="hidden sm:inline">{t(msg`Save failed`)}</span>
              </span>
            </Show>
            <Button
              variant="ghost"
              size="sm"
              class="shrink-0 gap-1.5"
              data-testid="version-history-open"
              onClick={() => setVersionsOpen(true)}
              aria-label={t(msg`Version history`)}
              title={t(msg`Version history`)}
            >
              <History class="h-4 w-4" />
              <span class="hidden sm:inline">{t(msg`History`)}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              class="shrink-0 gap-1.5"
              onClick={() => setShareOpen(true)}
              aria-label={t(msg`Share page`)}
              title={t(msg`Share page`)}
            >
              <Show when={page.data?.is_shared === 1} fallback={<Share2 class="h-4 w-4" />}>
                <Users class="h-4 w-4 text-accent" />
              </Show>
              <span class="hidden sm:inline">{t(msg`Share`)}</span>
            </Button>
          </div>
          <div class="mt-4 flex flex-1 flex-col">
            {/* The TipTap editor is the region most likely to break at runtime
              (documentos corruptos, extensiones): su propio ErrorBoundary evita
              que un fallo del editor tire toda la página. */}
            <AppErrorBoundary
              variant="region"
              label="editor"
              title={t(msg`The editor could not be loaded`)}
              description={t(
                msg`We could not render this note's content. Retry, or reload the page.`,
              )}
            >
              <Show
                when={editorState.isReady()}
                fallback={
                  <div class="flex flex-1 items-center justify-center">
                    <Loader2 class="h-5 w-5 animate-spin text-muted-foreground/50" />
                  </div>
                }
              >
                <PageEditor
                  initialDoc={editorState.initialDoc()}
                  onDocUpdate={editorState.handleDocUpdate}
                  onReady={editorState.handleReady}
                />
              </Show>
            </AppErrorBoundary>
          </div>
          <AppErrorBoundary variant="region" label="backlinks">
            <PageBacklinks pageId={props.pageId} />
          </AppErrorBoundary>
          <AppErrorBoundary variant="region" label="attachments">
            <PageAttachments
              pageId={props.pageId}
              expandedId={urlSearch().attachment ?? null}
              onExpandedChange={(id) => {
                void navigate({
                  to: ".",
                  search: (prev) => ({ ...prev, attachment: id ?? undefined }),
                  replace: true,
                });
              }}
            />
          </AppErrorBoundary>
        </div>
        <PageShareDialog pageId={props.pageId} open={shareOpen()} onOpenChange={setShareOpen} />
        <VersionHistorySheet
          open={versionsOpen()}
          onOpenChange={setVersionsOpen}
          pageId={() => props.pageId}
          currentTitle={() => page.data?.title ?? ""}
          currentBlocks={() => currentBlocks.data ?? []}
          onRestore={(version) => {
            void handleRestore(version);
          }}
          restoring={restoring}
        />
      </div>
    </Show>
  );
}
